import { useState } from "react";
import ChartTooltip from "./ChartTooltip.jsx";
import { formatNumber } from "../utils/format.js";
import styles from "./ConsistencyHeatmap.module.css";

const WEEKDAY_LABELS = ["Mon", "", "Wed", "", "Fri", "", ""];

const DAY_FORMAT = new Intl.DateTimeFormat("en-US", {
  weekday: "short", month: "short", day: "numeric", year: "numeric", timeZone: "UTC"
});

function level(count) {
  if (count == null) return styles.pad;
  if (count === 0) return styles.l0;
  if (count === 1) return styles.l1;
  if (count === 2) return styles.l2;
  return styles.l3;
}

function monthLabel(iso) {
  return new Intl.DateTimeFormat("en-US", { month: "short", timeZone: "UTC" }).format(new Date(`${iso}T00:00:00Z`));
}

// One label per month, placed on the week where that month first appears.
function buildMonthLabels(days, weeks) {
  const labels = [];
  let previous = null;
  for (let week = 0; week < weeks; week += 1) {
    const iso = days[week * 7]?.date;
    if (!iso) continue;
    const month = iso.slice(0, 7);
    if (month === previous) continue;
    previous = month;
    // A month that only owns the last column has no room for its label.
    if (week === weeks - 1) continue;
    labels.push({ week, text: monthLabel(iso) });
  }
  return labels;
}

function dayLabel(iso) {
  return DAY_FORMAT.format(new Date(`${iso}T00:00:00Z`));
}

function dayCount(count) {
  if (count === 0) return "Rest day";
  return `${count} workout${count === 1 ? "" : "s"}`;
}

export default function ConsistencyHeatmap({ consistency }) {
  const data = consistency;
  // The native `title` this replaces took about a second to appear, changed
  // nothing about the cell, and looked nothing like the other charts.
  const [hovered, setHovered] = useState(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });

  if (!data || !data.weeks) {
    return (
      <div className={styles.panel}>
        <div className={styles.heading}>
          <span className={styles.headLabel}>Consistency</span>
        </div>
        <div className={styles.empty}>No workouts to chart yet</div>
      </div>
    );
  }

  const { days, weeks, activeDays, activeWeeks, currentStreakWeeks, longestStreakWeeks, clampedToFirstWorkout, windowStart } = data;
  const months = buildMonthLabels(days, weeks);
  const range = clampedToFirstWorkout ? `since ${windowStart}` : `last ${weeks} weeks`;

  return (
    <div className={styles.panel}>
      <div className={styles.heading}>
        <span className={styles.headLabel}>Consistency</span>
        <span className={styles.total}>{range}</span>
      </div>

      <div className={styles.stats}>
        <div className={styles.stat}>
          <strong>{formatNumber(currentStreakWeeks)}</strong>
          <span>week streak</span>
        </div>
        <div className={styles.stat}>
          <strong>{formatNumber(longestStreakWeeks)}</strong>
          <span>longest streak</span>
        </div>
        <div className={styles.stat}>
          <strong>{formatNumber(activeWeeks)}<em>/{formatNumber(weeks)}</em></strong>
          <span>weeks trained</span>
        </div>
        <div className={styles.stat}>
          <strong>{formatNumber(activeDays)}</strong>
          <span>days trained</span>
        </div>
      </div>

      <div className={styles.scroller}>
        <div className={styles.chart} style={{ "--weeks": weeks }}>
          <div className={styles.months} aria-hidden="true">
            {months.map((m) => (
              <span key={m.week} style={{ gridColumn: m.week + 1 }}>{m.text}</span>
            ))}
          </div>
          <div className={styles.weekdays} aria-hidden="true">
            {WEEKDAY_LABELS.map((label, i) => (
              <span key={i}>{label}</span>
            ))}
          </div>
          <div className={styles.grid} role="img" aria-label={`Workout days over the ${range}: ${activeDays} days trained across ${activeWeeks} of ${weeks} weeks.`}>
            {days.map((day) => (
              <span
                key={day.date}
                className={`${styles.cell} ${level(day.count)}`}
                onMouseEnter={day.count == null ? undefined : (event) => {
                  setHovered(day);
                  setPos({ x: event.clientX, y: event.clientY });
                }}
                onMouseMove={day.count == null ? undefined : (event) => setPos({ x: event.clientX, y: event.clientY })}
                onMouseLeave={day.count == null ? undefined : () => setHovered(null)}
              />
            ))}
          </div>
        </div>
      </div>

      <div className={styles.legend} aria-hidden="true">
        <span>Less</span>
        <i className={`${styles.cell} ${styles.l0}`} />
        <i className={`${styles.cell} ${styles.l1}`} />
        <i className={`${styles.cell} ${styles.l2}`} />
        <i className={`${styles.cell} ${styles.l3}`} />
        <span>More</span>
      </div>

      {hovered && (
        <ChartTooltip className={styles.tooltip} x={pos.x} y={pos.y}>
          <span className={styles.tooltipLabel}>{dayLabel(hovered.date)}</span>
          <span className={styles.tooltipValue}>{dayCount(hovered.count)}</span>
        </ChartTooltip>
      )}
    </div>
  );
}
